import { ChangeDetectionStrategy, Component, computed, input, numberAttribute } from '@angular/core';

export type LogoVariant = 'primary' | 'mono' | 'endorsed' | 'core' | 'catalog';

interface LogoAsset {
  readonly src: string;
  readonly alt: string;
  /** Intrinsic ratio from the file's viewBox, so the box is reserved up front. */
  readonly ratio: number;
}

/** Each lockup keeps its own colour; none is repainted to suit the background it lands on. */
const ASSETS: Record<LogoVariant, LogoAsset> = {
  primary: {
    src: 'brand/avyna.webp',
    alt: 'Avyna',
    ratio: 952 / 240,
  },
  mono: {
    src: 'brand/avyna-inverse.webp',
    alt: 'Avyna',
    ratio: 952 / 240,
  },
  endorsed: {
    src: 'brand/avyna-algocreed.svg',
    alt: 'Avyna by AlgoCreed',
    ratio: 1080 / 235.95,
  },
  core: {
    src: 'brand/avyna-core.svg',
    alt: 'Avyna Core',
    ratio: 1094.06 / 248.84,
  },
  catalog: {
    src: 'brand/avyna-catalog.svg',
    alt: 'Avyna Catalog',
    ratio: 1080 / 222.84,
  },
};

@Component({
  selector: 'av-logo',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <img
      [src]="src()"
      [alt]="label()"
      [width]="width()"
      [height]="height()"
      [style.height.px]="height()"
      decoding="async"
      [attr.fetchpriority]="priority() ? 'high' : null"
      [attr.loading]="priority() ? null : 'lazy'"
    />
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        line-height: 0;
      }

      img {
        width: auto;
        display: block;
        /* Costs nothing where the height never changes, which is everywhere but
           the header; the width follows the ratio on its own as it runs. */
        transition: height var(--dur-2) var(--ease-out);
        /* The bar can squeeze the lockup narrower than its ratio; letterbox it
           rather than stretch it, and keep it flush with the left edge. */
        object-fit: contain;
        object-position: left center;
      }
    `,
  ],
})
export class Logo {
  readonly variant = input<LogoVariant>('primary');
  readonly height = input(24, { transform: numberAttribute });

  /**
   * Set on the header and the product hero — the lockup is the first thing a
   * visitor looks for, and it is above the fold. Everything else can wait.
   */
  readonly priority = input(false);

  /** Overrides the alt text where the lockup sits inside an already-labelled link. */
  readonly alt = input<string | null>(null);

  private readonly asset = computed(() => ASSETS[this.variant()]);

  protected readonly src = computed(() => this.asset().src);

  protected readonly label = computed(() => this.alt() ?? this.asset().alt);

  /** Reserves the right box before the SVG lands, so nothing reflows. */
  protected readonly width = computed(() => Math.round(this.height() * this.asset().ratio));
}
